import { Component, inject, Input } from '@angular/core';
import { AsyncPipe, NgClass } from '@angular/common';
import { BlueprintSelectorModalComponent } from '../../blueprints-viewer/blueprint-selector-modal/blueprint-selector-modal.component';
import { Button } from 'primeng/button';
import { Droppable } from 'primeng/dragdrop';
import { MessageModule } from 'primeng/message';
import { Tooltip } from 'primeng/tooltip';
import { BlueprintItemDataEditorService } from './blueprint-item-data-editor.service';
import { Blueprint } from '../../../models/blueprint.model';
import { QuoteItemData } from '../../../models/quote-item-data.model';
import { BlueprintItemDataTree } from './blueprint-item-data-tree/blueprint-item-data-tree.component';
import {
  CIRCULAR_BLUEPRINT_REFERENCE_MESSAGE,
  getBlueprintsAllowedToAdd,
  linkedBlueprintCreatesCycle,
} from './blueprint-allowed-to-add.util';
import { Equipment } from '../../../models/equipment.model';
import { Consumable } from '../../../models/consumable.model';

@Component({
  selector: 'app-blueprint-item-data-editor',
  imports: [
    AsyncPipe,
    NgClass,
    BlueprintSelectorModalComponent,
    Button,
    Droppable,
    Tooltip,
    BlueprintItemDataTree,
    MessageModule,
  ],
  templateUrl: './blueprint-item-data-editor.component.html',
  styleUrl: './blueprint-item-data-editor.component.scss',
})
export class BlueprintItemDataEditorComponent {
  quoteItemDataEditorService: BlueprintItemDataEditorService = inject(
    BlueprintItemDataEditorService,
  );

  @Input() blueprint: Blueprint | null = null;
  @Input() allBlueprints: Blueprint[] = [];
  @Input() equipmentOptions: Equipment[] = [];
  @Input() consumableOptions: Consumable[] = [];
  @Input() tradeStudyToolFeaturesEnabled = false;
  @Input() consumablesEnabled = false;

  showBlueprintSelector = false;
  allowedBlueprintsForAdd: Blueprint[] = [];
  selectorNotice: string | null = null;
  linkError: string | null = null;

  @Input() currencyCode?: string;

  get rootItems(): QuoteItemData[] {
    const items = this.quoteItemDataEditorService.items();
    const rootItems = items.filter((item) => item.parent_id === null);
    return this.quoteItemDataEditorService.sortSiblingsBySiblingOrder(
      rootItems,
    );
  }

  onAddBlueprint(): void {
    this.linkError = null;
    this.allowedBlueprintsForAdd = getBlueprintsAllowedToAdd(
      this.blueprint,
      this.allBlueprints,
    );
    const contextId = this.blueprint == null ? null : String(this.blueprint.id);
    const hiddenBecauseTheyLoop = this.allBlueprints.filter((blueprint) => {
      const id = String(blueprint.id);
      if (contextId != null && id === contextId) {
        return false;
      }
      return !this.allowedBlueprintsForAdd.some(
        (allowed) => String(allowed.id) === id,
      );
    });
    this.selectorNotice =
      hiddenBecauseTheyLoop.length > 0
        ? CIRCULAR_BLUEPRINT_REFERENCE_MESSAGE
        : null;
    this.showBlueprintSelector = true;
  }

  onBlueprintSelected(blueprint: Blueprint): void {
    this.addBlueprint(blueprint);
  }

  addBlueprint(blueprint: Blueprint): void {
    // The following alert should never be triggered because disallowed blueprints are filtered when the list to select
    // from is generated, but it's here as a safeguard against future changes that might bypass the selection UI.
    if (
      linkedBlueprintCreatesCycle(
        this.blueprint,
        this.allBlueprints,
        blueprint.id,
      )
    ) {
      this.linkError = CIRCULAR_BLUEPRINT_REFERENCE_MESSAGE;
      return;
    }
    this.linkError = null;
    this.quoteItemDataEditorService.addBlueprintItem(blueprint, null);
  }

  onAddBlankItem(): void {
    this.quoteItemDataEditorService.createBlankItem();
  }

  onAddChildToItem(parentId: string): void {
    // Prevent adding children to referenced blueprint items
    if (parentId.startsWith('ref-')) {
      return;
    }
    this.quoteItemDataEditorService.createBlankItem(undefined, parentId);
  }

  onRemoveItem(itemPersistentId: string): void {
    this.quoteItemDataEditorService.removeItem(itemPersistentId);
  }
}
